'use client';

import React, { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { CacheKeys, ENGRAVING_FORM_MODAL_ID, Position, ServerActionName } from '@headless-workspace/config';
import { SideMenuModal, useToasts, useUIContext } from '@headless-workspace/core-ui';
import {
    CardBackground,
    CardSize,
    getIcon,
    GlowTag,
    GlowTextBody,
    GlowTile,
    IconName,
    PropsWithFontFamily,
} from '@headless-workspace/glow-ds';
import { Component, ImageWithSrc } from '@headless-workspace/typings';
import {
    EngraveFormValue,
    getProductFormServerAction,
    ProductEngraveFormModal,
    ProductFormId,
    ProductType,
    useEngraveContext,
    useProductDetailContext,
    useSafeSWRMutation,
} from '../../../../src';

export type ProductEngraveContainerProps = PropsWithFontFamily & {
    productImage?: ImageWithSrc;
};

export const ProductEngraveContainer: Component<ProductEngraveContainerProps> = ({ productImage, fontFamily }) => {
    const t = useTranslations('Modal.engrave');
    const formT = useTranslations('Common.form');
    const { productDetails } = useProductDetailContext();
    const {
        menuModal: { addIfAbsentAndOpen },
    } = useUIContext();
    const { notifyError } = useToasts();
    const { engraveFormValues } = useEngraveContext();

    const { trigger: fetchEngraveFormData, isMutating } = useSafeSWRMutation(
        CacheKeys.EngravingForm,
        ServerActionName.getProductForm,
        (_, { arg }: { arg: { productId: string } }) =>
            getProductFormServerAction(ProductFormId.Engrave, arg.productId),
        {
            onSuccess: (result) => {
                if (result.type === 'success') {
                    const formValueTemplate: EngraveFormValue = result.data.product.image
                        ? result.data
                        : {
                              ...result.data,
                              product: { ...result.data.product, image: productImage },
                          };

                    const modalInfo = {
                        modalId: ENGRAVING_FORM_MODAL_ID,
                        modal: SideMenuModal({
                            side: Position.Right,
                            map: {
                                default: <ProductEngraveFormModal formValueTemplate={formValueTemplate} />,
                            },
                            removeOnClose: true,
                        }),
                    };
                    addIfAbsentAndOpen(modalInfo.modalId, modalInfo.modal, true);
                } else {
                    notifyError(formT('error.getForm'));
                }
            },
        },
    );

    const onEngraveButtonClick = () => {
        if (productDetails.type === ProductType.PRODUCT_SET) {
            return;
        }
        void fetchEngraveFormData({ productId: productDetails.sku });
    };

    const engraveLinesStringified = useMemo(() => {
        if (!engraveFormValues) {
            return '';
        }

        return Object.entries(engraveFormValues)
            .reduce((acc, [key, value]) => {
                if (key !== 'font' && value) {
                    return [...acc, value];
                }
                return acc;
            }, new Array<string>())
            .join(' - ');
    }, [engraveFormValues]);

    const hasEngravePendingOptions = engraveLinesStringified.length > 0;

    if (productDetails.type === ProductType.PRODUCT_SET) {
        // Engraving not supported for PRODUCT_SET products
        return null;
    }

    return (
        <GlowTile
            content={{
                hasRightIcon: true,
                Icon: getIcon(IconName.Perfum),
                isLoading: isMutating,
                className: '!h-[64px]',
            }}
            background={CardBackground.Neutral}
            TagName={'button'}
            size={CardSize.S}
            onClick={onEngraveButtonClick}
        >
            <div className={'flex flex-col items-start'}>
                <div className={'flex flex-row gap-0.5'}>
                    <GlowTextBody
                        fontFamily={fontFamily}
                        text={hasEngravePendingOptions ? t('cta.editEngrave') : t('cta.addEngrave')}
                        fontWeight={'bold'}
                    />
                    <GlowTag fontFamily={fontFamily} label={t('cta.freeTag')} color={'secondaryGreen'} size={'small'} />
                </div>
                {hasEngravePendingOptions && (
                    <GlowTextBody fontFamily={fontFamily} text={engraveLinesStringified} color={'secondary'} />
                )}
            </div>
        </GlowTile>
    );
};
